/**
 * STORY#3197 专业版线上购买 —— 接口层（payment 服务）
 *
 * 接口文档：https://uat.snkoudai.com/api/payment/v2/api-docs?group=coApi
 * 统一 POST + JSON，响应外壳 BaseResp：respCode(0 成功) / respDesc / obj / curDate。
 * 请求头带 accessToken 与 companyId（companyId 头在 @/utils/request.js 统一注入）。
 *
 * 本层职责：真实请求 + 「接口 DTO → 视图模型」映射。
 * 页面组件只消费视图模型（status/goodsName/price/...），后端字段调整只需改这里的 mapper。
 */
import axios from "@/utils/request";
import { snkd } from "@/api/api";
import { getStorage } from "@/utils/storage";
import { MOCK_RIGHTS } from "@/views/ProPlan/mock";
import {
  ORDER_SOURCE,
  PAY_TERMINAL,
  PAY_METHOD_TEXT,
  PAY_RESULT,
  ORDER_STATUS,
  QUALIFY_REASON,
  ORDER_PAGE_SIZE,
} from "@/consts/proPlan";
import { calcUnitPrice, durationToDays, toTimestamp } from "@/utils/proPlan";

const URLS = snkd.payment.proPlan;

/**
 * 取 companyId：优先入参，其次本地缓存（农场/企业信息）
 *
 * ⚠️ 调用方务必显式传 companyId（通常来自路由 `$route.query.companyId`）。
 * 省略时会回落到本地缓存企业，若与页面所属企业不一致，按 orderId 查单会返回
 * 42002「订单不存在或不属于当前企业」——曾导致确认页倒计时归零查单失败误跳过期页。
 */
function resolveCompanyId(params) {
  if (params && params.companyId) return params.companyId;
  const farmInfo = getStorage("FARMINFO") || {};
  const companyInfo = getStorage("COMPANYINFO") || {};
  return farmInfo.companyId || companyInfo.companyId || null;
}

/**
 * 统一 POST：把 companyId 同时放进 body 与 config，
 * 便于请求拦截器用同一个值写 header，避免 body/header 归属不一致（会触发鉴权类错误）
 */
function post(url, data) {
  const companyId = data && data.companyId;
  return axios.post(url, data, companyId ? { companyId } : undefined);
}

/**
 * 保留 respCode/respDesc，把 obj 交给 mapper 转成视图模型
 * @param {Object} res 原始 BaseResp
 * @param {Function} [mapper] (obj, res) => 视图模型
 */
function mapRes(res, mapper) {
  const out = {
    respCode: res.respCode,
    respDesc: res.respDesc,
    curDate: res.curDate,
  };
  out.obj = mapper ? mapper(res.obj, res) : res.obj;
  return out;
}

/**
 * 套餐 DTO(/paymentPackage/list) → 视图模型
 * 接口字段：packageId / packageName / packageSubName / packageDuration(月,2位小数) / packagePrice(元,2位小数)
 * 说明（产品已确认）：套餐顺序由后端保证，前端不排序，默认选中接口返回的第一个；
 *                    价格与时长直接使用接口值（2 位小数）。
 */
function mapPackage(dto) {
  const d = dto || {};
  const months = Number(d.packageDuration) || 0;
  const days = durationToDays(months);
  return {
    id: d.packageId, // 组件以 id 作选中标识
    packageId: d.packageId,
    name: d.packageName || "",
    subName: d.packageSubName || "",
    price: d.packagePrice,
    months,
    days,
    unitPrice: calcUnitPrice(d.packagePrice, days),
  };
}

/**
 * 订单 DTO(CoOrderVO) → 视图模型
 * 接口字段（实测 list / get 返回）：orderId / orderNo / companyName / packageName / packageSubName /
 *   packageDuration / payAmount / payMethodName / orderStatus / createTime / effectiveTime /
 *   entitlementEnd / refundTime / cancelTime
 * 说明：支付方式由后端直接下发文案 `payMethodName`（如「微信支付」），前端不再用数字码映射。
 */
function mapOrder(dto) {
  const d = dto || {};
  return {
    id: d.orderId,
    orderId: d.orderId,
    orderNo: d.orderNo,
    // 开通企业名称：设计稿确认页「开通企业」下方展示的即此字段（禅道 Bug#23429-2）
    companyName: d.companyName || "",
    status: d.orderStatus,
    goodsName: d.packageName || "",
    packageSubName: d.packageSubName || "",
    months: d.packageDuration,
    price: d.payAmount,
    // 支付方式：优先接口文案；兼容旧字段 payMethod(数字码) 兜底映射
    payType: d.payMethodName || PAY_METHOD_TEXT[d.payMethod] || "",
    payMethod: d.payMethod,
    orderSource: d.orderSource,
    createTime: d.createTime,
    payTime: d.payTime,
    cancelTime: d.cancelTime,
    effectTime: d.effectiveTime,
    expireTime: d.entitlementEnd,
    refundAmount: d.refundAmount,
    refundTime: d.refundTime,
    // 列表接口不返回 codeUrl/packageId，若后端补充则自动生效
    // （「去支付」当前通过 /paymentOrder/pending 取回链接，见 OrderList.handlePay）
    codeUrl: d.codeUrl,
    packageId: d.packageId,
  };
}

/**
 * 购买资格/版本状态(/paymentOrder/qualification) → 状态条所需字段
 * 接口字段：qualified / rankType(0免费 1专业 2旗舰) / canRenew / expireDate / reasonCode / reasonDesc
 * 与老端 buildProBannerHtml 的 status 结构保持同构，便于共用状态条样式。
 */
function mapQualification(dto, res) {
  const d = dto || {};
  const rankType = Number(d.rankType) || 0;
  const now = toTimestamp(res && res.curDate) || Date.now();
  const expireTs = toTimestamp(d.expireDate);
  const hasExpire = !isNaN(expireTs);
  // 过期判定按「日期」比较：需求 §2 为「到期日期 < 当前日期」才算过期，
  // 即当天到期仍属有效（老端 getCompanyStatusInfo 也是与当天 0 点比较，需保持一致）
  const nowDate = new Date(now);
  const todayStart = new Date(
    nowDate.getFullYear(),
    nowDate.getMonth(),
    nowDate.getDate()
  ).getTime();
  // 接口契约中 reasonCode 为 string（"41004"），但错误码也可能以 respCode 数字返回，
  // 故统一转成数字再比对，避免 "41004" !== 41004 导致下游分流失效
  const knownReasons = Object.values(QUALIFY_REASON);
  const rawReason =
    d.reasonCode !== undefined && d.reasonCode !== null && d.reasonCode !== ""
      ? d.reasonCode
      : res && res.respCode;
  const reasonNum = Number(rawReason);
  // 仅当取到已知原因码时才写入，成功时（respCode 0）保持为空，避免下游误读
  const reasonCode = knownReasons.includes(reasonNum) ? reasonNum : undefined;
  return {
    // —— 业务判断用（页面直接消费）——
    // qualified 缺失时（如接口直接以错误码返回）按 reasonCode 判断
    qualified:
      d.qualified !== undefined
        ? !!d.qualified
        : !knownReasons.includes(Number(reasonCode)),
    reasonCode,
    reasonDesc: d.reasonDesc || (res && res.respDesc) || "",
    // 保留「未下发」语义（不要用 !! 强制转 false）：状态条在字段缺失时要退回
    // 「按剩余天数判断是否展示去续费」的兜底逻辑，否则该分支永远不会命中
    canRenew:
      d.canRenew === undefined || d.canRenew === null
        ? undefined
        : !!d.canRenew,
    // —— 状态条展示用 ——
    rankType,
    version: rankType === 2 ? "旗舰版" : rankType === 1 ? "专业版" : "免费版",
    // 收费开关：优先取运营商的 rankOpenFlag（与老端 getCompanyStatusInfo 的 isOpenCharge 同源，
    // 且资格接口自身也会返回该字段）；缺失时退回「reasonCode 非 41003」判断
    isOpenCharge:
      d.rankOpenFlag !== undefined && d.rankOpenFlag !== null
        ? !!d.rankOpenFlag
        : Number(reasonCode) !== QUALIFY_REASON.CHARGE_CLOSED,
    professionalEndTime: hasExpire ? expireTs : null,
    nowTime: now,
    // 专业版且到期日早于今天（或未给到期日）
    isFailureTime: rankType > 0 && (!hasExpire || expireTs < todayStart),
    // 当前专业版到期日期（"yyyy-MM-dd"，免费版/未下发为 null）。
    // ⚠️ 过期企业也必须有值：生效日期 = 到期日 +1 天（可早于下单日），
    // 与管理后台 effectiveDate 及服务端口径一致（见 utils/proPlan.calcValidPeriod）。
    expireDate: hasExpire ? d.expireDate : null,
  };
}

/** 套餐列表 */
export const getProPlanList = async (params = {}) => {
  const res = await post(URLS.packageList, {
    companyId: resolveCompanyId(params),
  });
  return mapRes(res, (obj) => (obj || []).map(mapPackage));
};

/** 9 大权益对比：后端无此接口，保持前端静态数据 */
export const getProPlanRights = async () => {
  return { respCode: 0, obj: MOCK_RIGHTS };
};

/** 企业购买资格与版本状态（状态条 + 购买入口判断） */
export const getCompanyPlanStatus = async (params = {}) => {
  const res = await post(URLS.qualification, {
    companyId: resolveCompanyId(params),
  });
  return mapRes(res, (obj, raw) => mapQualification(obj, raw));
};

/**
 * 可用支付方式（terminal: 1 WEB / 2 APP）
 * 出参：methodName / payMethod(1 微信支付)
 * 本期仅实现微信扫码，暂未在页面消费；待接入多支付方式时由确认页调用它决定展示项。
 */
export const getPaymentMethodList = async (params = {}) => {
  const res = await post(URLS.methodList, {
    companyId: resolveCompanyId(params),
    terminal: PAY_TERMINAL.WEB,
  });
  return mapRes(res, (obj) => obj || []);
};

/**
 * 创建订单（web 扫码 Native）
 * @param {Object} params { packageId, continueFlag, companyId }
 * 出参：codeUrl(微信二维码链接) / expireTime(yyyy-MM-dd HH:mm:ss) / orderNo / orderStatus / prepayInfo
 * ⚠️ 42001（有待支付订单）、42003（订单已支付）由调用方按 respCode 处理，本层不吞错误码。
 */
export const createProOrder = async (params = {}) => {
  const res = await post(URLS.orderAdd, {
    companyId: resolveCompanyId(params),
    continueFlag: !!params.continueFlag,
    orderSource: ORDER_SOURCE.WEB,
    packageId: params.packageId,
  });
  return mapRes(res, (obj) => {
    const d = obj || {};
    return {
      orderId: d.orderId || d.id,
      orderNo: d.orderNo,
      status: d.orderStatus,
      codeUrl: d.codeUrl,
      expireTime: d.expireTime,
      prepayInfo: d.prepayInfo,
    };
  });
};

/** 订单详情 */
export const getProOrderDetail = async (params = {}) => {
  const res = await post(URLS.orderGet, {
    companyId: resolveCompanyId(params),
    orderId: params.orderId,
  });
  return mapRes(res, mapOrder);
};

/**
 * 支付状态查询（轮询用）
 * 出参：orderStatus / effectiveTime / entitlementEnd（仅 status=20 时返回后两者）
 */
export const queryProOrderPayStatus = async (params = {}) => {
  const res = await post(URLS.orderPayStatus, {
    companyId: resolveCompanyId(params),
    orderId: params.orderId,
  });
  return mapRes(res, (obj) => {
    const d = obj || {};
    return {
      paid: Number(d.orderStatus) === ORDER_STATUS.PAID,
      status: d.orderStatus,
      effectTime: d.effectiveTime,
      expireTime: d.entitlementEnd,
    };
  });
};

/**
 * 「我已支付」→ 主动查单（paymentOrder/verifyPayStatus）
 * 出参：orderStatus / payResult(SUCCESS 成功 / PROGRESS 处理中 / FAIL 失败) /
 *      effectiveTime / entitlementEnd（支付成功时返回）
 * ⚠️ PROGRESS 表示渠道仍在处理，不可判为失败，由调用方提示继续等待。
 */
export const confirmProOrderPaid = async (params = {}) => {
  const res = await post(URLS.orderVerifyPayStatus, {
    companyId: resolveCompanyId(params),
    orderId: params.orderId,
  });
  return mapRes(res, (obj) => {
    const d = obj || {};
    return {
      status: d.orderStatus,
      payResult: d.payResult,
      paid: d.payResult === PAY_RESULT.SUCCESS,
      effectTime: d.effectiveTime,
      expireTime: d.entitlementEnd,
    };
  });
};

/**
 * 待支付订单（paymentOrder/pending）
 * 出参：codeUrl（复用落库链接重渲染）/ expireTime / orderId / orderNo / orderStatus(10) / prepayInfo
 * 用途：【前往支付】直接取旧单的二维码与有效期，无需重新下单；无待支付单时 obj 为空。
 */
export const getPendingProOrder = async (params = {}) => {
  const res = await post(URLS.orderPending, {
    companyId: resolveCompanyId(params),
  });
  return mapRes(res, (obj) => {
    const d = obj || {};
    if (!d.orderId) return null;
    return {
      orderId: d.orderId,
      orderNo: d.orderNo,
      status: d.orderStatus,
      codeUrl: d.codeUrl,
      expireTime: d.expireTime,
      prepayInfo: d.prepayInfo,
    };
  });
};

/**
 * 取消待支付订单（paymentOrder/cancel）
 * 出参：orderStatus（30 已取消；42003 场景可能为 20 已支付）
 * ⚠️ 42003（订单已支付）由调用方按 respCode 处理。
 */
export const cancelProOrder = async (params = {}) => {
  const res = await post(URLS.orderCancel, {
    companyId: resolveCompanyId(params),
    orderId: params.orderId,
  });
  return mapRes(res, (obj) => {
    const d = obj || {};
    return { status: d.orderStatus };
  });
};

/**
 * 订单列表（分页）
 * 出参：list[](CoOrderVO) / totalCount；接口已按下单时间倒序，前端不再二次排序
 */
export const getProOrderList = async (params = {}) => {
  const res = await post(URLS.orderList, {
    companyId: resolveCompanyId(params),
    pageNo: params.pageNo || 1,
    pageSize: params.pageSize || ORDER_PAGE_SIZE,
  });
  return mapRes(res, (obj) => {
    const d = obj || {};
    return {
      list: (d.list || []).map(mapOrder),
      totalCount: d.totalCount || 0,
    };
  });
};
